import { createRoute, Link, useSearch } from "@tanstack/solid-router";
import { createSignal, Match, onMount, Switch } from "solid-js";
import { authRoute } from "./route";
import { api } from "@/lib/api";
import { sharedApi } from "@listto/shared";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/logo";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { GlassSurface } from "@/components/ui/glass-surface";
import { CheckCircle2, Loader2, XCircle } from "lucide-solid";

/**
 * Página de verificación de email: consume el token del enlace enviado por
 * correo (`/verify-email?token=…`) contra el API y muestra el resultado.
 * El enlace caduca a las 24 h; en ese caso se ofrece reenviar desde el perfil.
 */
function VerifyEmailPage() {
  const search = useSearch({ from: "/_auth/verify-email" });
  const { t } = useI18n();
  const [status, setStatus] = createSignal<"loading" | "success" | "error">("loading");

  onMount(() => {
    void (async () => {
      const { token } = search();
      if (!token) {
        setStatus("error");
        return;
      }
      try {
        const res = await sharedApi.verifyEmail(api, { token });
        setStatus(res.error ? "error" : "success");
      } catch {
        setStatus("error");
      }
    })();
  });

  return (
    <GlassSurface borderEdge="accent">
      <CardHeader class="flex flex-col items-center space-y-4 pt-10 pb-6">
        <Link to="/" class="transition-transform hover:scale-105 active:scale-95">
          <Logo size="lg" />
        </Link>
        <CardTitle class="text-2xl">{t(msg`Email verification`)}</CardTitle>
        <CardDescription class="text-center">
          {t(msg`Confirming your email address`)}
        </CardDescription>
      </CardHeader>

      <CardContent class="space-y-4">
        <Switch>
          <Match when={status() === "loading"}>
            <div
              class="flex items-center justify-center gap-3 p-4 text-sm text-muted-foreground"
              data-testid="verify-loading"
            >
              <Loader2 class="h-5 w-5 animate-spin" />
              {t(msg`Verifying your email…`)}
            </div>
          </Match>
          <Match when={status() === "success"}>
            <div
              class="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm text-center space-y-1"
              data-testid="verify-success"
            >
              <CheckCircle2 class="mx-auto h-8 w-8 text-primary" />
              <p class="font-medium">{t(msg`Email verified!`)}</p>
              <p class="text-muted-foreground">
                {t(msg`Your email address has been confirmed. You can now continue using the app.`)}
              </p>
            </div>
          </Match>
          <Match when={status() === "error"}>
            <div
              class="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-center space-y-1"
              data-testid="verify-error"
            >
              <XCircle class="mx-auto h-8 w-8 text-destructive" />
              <p class="font-medium text-destructive">{t(msg`Invalid link`)}</p>
              <p class="text-muted-foreground">
                {t(
                  msg`This verification link is invalid or has expired. You can request a new one from your profile.`,
                )}
              </p>
            </div>
          </Match>
        </Switch>
      </CardContent>

      <CardFooter class="flex flex-col gap-3">
        <Link to="/" class="w-full">
          <Button class="w-full">{t(msg`Go to the app`)}</Button>
        </Link>
        <p class="text-center text-sm text-muted-foreground">
          <Link to="/login" class="text-primary underline-offset-4 hover:underline font-medium">
            {t(msg`Back to sign in`)}
          </Link>
        </p>
      </CardFooter>
    </GlassSurface>
  );
}

export const verifyEmailRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/verify-email",
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: VerifyEmailPage,
});
